<div class="flights-page">
    <h2 class="title">{{ "FLIGHT_LIST.title" | translate }}</h2>

    <kendo-grid
        class="flights-grid"
        [data]="rows()"
        [rowSticky]="isPinned"
        [skip]="state().skip"
        [pageSize]="state().take"
        [sort]="state().sort"
        [filter]="state().filter"
        [pageable]="{ pageSizes: pageSizes, info: true }"
        [sortable]="{ allowUnsort: true, mode: 'single' }"
        filterable="row"
        [loading]="loading()"
        [scrollable]="'scrollable'"
        [resizable]="true"
        (dataStateChange)="dataStateChange($event)"
    >
        <kendo-toolbar>
            <app-toolbar-slot>
                <span class="tool-label">{{ "FLIGHT_LIST.aircraft" | translate }}</span>
                <kendo-multiselect
                    [formControl]="search.controls.helicopterIds"
                    [data]="aircraft()"
                    textField="registrationMark"
                    valueField="id"
                    [valuePrimitive]="true"
                    [checkboxes]="true"
                    [autoClose]="false"
                    [kendoDropDownFilter]="{ operator: 'contains' }"
                    [kendoMultiSelectSummaryTag]="2"
                    [placeholder]="'FLIGHT_LIST.allAircraft' | translate"
                    [style.width.px]="220"
                >
                    <ng-template kendoMultiSelectGroupTagTemplate let-dataItems>
                        {{ "FLIGHT_LIST.aircraftCount" | translate: { count: dataItems.length } }}
                    </ng-template>
                </kendo-multiselect>
            </app-toolbar-slot>
            <!-- Each date bounds the other, so the calendar never offers a range that ends before it starts. -->
            <app-toolbar-slot>
                <span class="tool-label">{{ "FLIGHT_LIST.dateFrom" | translate }}</span>
                <kendo-datepicker [formControl]="search.controls.from" format="dd/MM/yyyy" [max]="search.controls.to.value ?? today" [style.width.px]="150"></kendo-datepicker>
            </app-toolbar-slot>
            <app-toolbar-slot>
                <span class="tool-label">{{ "FLIGHT_LIST.dateTo" | translate }}</span>
                <kendo-datepicker [formControl]="search.controls.to" format="dd/MM/yyyy" [min]="search.controls.from.value" [max]="today" [style.width.px]="150"></kendo-datepicker>
            </app-toolbar-slot>
            <app-toolbar-slot>
                <span class="tool-label">{{ "FLIGHT_LIST.timeZone.label" | translate }}</span>
                <kendo-dropdownlist [formControl]="timeZoneKey" [data]="timeZones()" textField="label" valueField="key" [valuePrimitive]="true" [style.width.px]="270">
                    <ng-template kendoDropDownListValueTemplate let-option>{{ option.label }}</ng-template>
                    <ng-template kendoDropDownListItemTemplate let-option>
                        {{ option.label }}
                        @if (option.key !== "utc") {
                            <small class="zone-kind">{{ "FLIGHT_LIST.timeZone." + option.key | translate }}</small>
                        }
                    </ng-template>
                </kendo-dropdownlist>
            </app-toolbar-slot>
            @if (searchError(); as error) {
                <app-toolbar-slot>
                    <span class="search-error">{{ error | translate }}</span>
                </app-toolbar-slot>
            }
            <!-- Recreated by clearFilters: the box offers no way to empty its text. -->
            @for (key of [searchBoxKey()]; track key) {
                <kendo-grid-smartbox-tool
                    [searchMode]="searchSettings()"
                    [semanticSearchMode]="false"
                    [aiAssistantMode]="false"
                    (search)="onSearch($event)"
                ></kendo-grid-smartbox-tool>
            }
            <kendo-toolbar-spacer></kendo-toolbar-spacer>
            <kendo-toolbar-button [text]="'FLIGHT_LIST.clearFilters' | translate" [svgIcon]="icons.clear" fillMode="flat" (click)="clearFilters()"></kendo-toolbar-button>
            <kendo-toolbar-button kendoGridExcelTool [text]="'FLIGHT_LIST.export' | translate" [svgIcon]="icons.excel" themeColor="primary" fillMode="solid"></kendo-toolbar-button>
        </kendo-toolbar>
        <kendo-grid-excel [fileName]="excelFileName()" [fetchData]="exportData"></kendo-grid-excel>

        <kendo-grid-command-column
            [width]="pinColumn.width"
            [resizable]="false"
            [class]="'pin-cell sticky-left'"
            [headerClass]="'sticky-left'"
            [filterClass]="'sticky-left'"
            [style]="{ left: pinColumn.left }"
            [headerStyle]="{ left: pinColumn.left }"
            [filterStyle]="{ left: pinColumn.left }"
        >
            <ng-template kendoGridCellTemplate let-row>
                @let pinned = pinnedIds().has(row.id);
                <button
                    kendoButton
                    fillMode="flat"
                    themeColor="base"
                    [svgIcon]="pinned ? icons.unpin : icons.pin"
                    [title]="(pinned ? 'FLIGHT_LIST.unpin' : 'FLIGHT_LIST.pin') | translate"
                    (click)="togglePin(row)"
                ></button>
            </ng-template>
        </kendo-grid-command-column>

        <ng-template #cell let-row let-column="column">
            @let value = row | fieldValue: column.field;
            @switch (column.type) {
                @case ("boolean") {
                    @if (value) {
                        <kendo-svgicon class="yes" [icon]="icons.yes"></kendo-svgicon>
                    }
                }
                @case ("decimal") {
                    {{ value | fixed }}
                }
                @case ("time") {
                    {{ value | logTime }}
                }
                @case ("date") {
                    {{ value | logDate }}
                }
                @case ("dateTime") {
                    {{ value | zonedDateTime: timeZone() }}
                }
                @case ("hook") {
                    <!-- Serial and name only on hover, to keep the columns narrow; one line per hook mounted. -->
                    @for (hook of row.hooks; track hook.id) {
                        <div class="hook" (mouseenter)="showHook($event.target, hook)" (mouseleave)="hint.set(null)">
                            {{ column.hookField === "hours" ? (hook.hours | logTime) : hook[column.hookField] }}
                        </div>
                    }
                }
                @case ("hoist") {
                    <!-- Serial and name only on hover, to keep the columns narrow; one line per hoist mounted. -->
                    @for (hoist of row.hoists; track hoist.id) {
                        <div class="hoist" (mouseenter)="showHoist($event.target, hoist)" (mouseleave)="hint.set(null)">
                            {{ column.hoistField === "hours" ? (hoist.hours | logTime) : hoist[column.hoistField] }}
                        </div>
                    }
                }
                @case ("place") {
                    <span class="place" (mouseenter)="showAirport($event.target, row, column.airportField)" (mouseleave)="hint.set(null)">{{ value }}</span>
                }
                @case ("pilotRole") {
                    <span class="role" (mouseenter)="showRole($event.target, value)" (mouseleave)="hint.set(null)">{{ value }}</span>
                }
                @default {
                    {{ value }}
                }
            }
        </ng-template>

        @for (group of groups(); track group.title) {
            <kendo-grid-column-group [title]="group.title | translate" [headerClass]="'group-header'">
                @for (item of group.items; track $index) {
                    @if ("columns" in item) {
                        <kendo-grid-column-group [title]="item.title | translate" [headerClass]="'group-header'">
                            @for (column of item.columns; track column.field) {
                                <kendo-grid-column
                                    [field]="column.field"
                                    [title]="column.title | translate"
                                    [width]="column.width"
                                    [filterable]="!!column.filterable"
                                    [sortable]="!!column.sortable"
                                    [filter]="column.type === 'number' ? 'numeric' : column.type === 'boolean' ? 'boolean' : 'text'"
                                    [class]="column.left ? 'left' : 'centered'"
                                >
                                    <ng-template kendoGridCellTemplate let-row>
                                        <ng-container *ngTemplateOutlet="cell; context: { $implicit: row, column: column }"></ng-container>
                                    </ng-template>
                                </kendo-grid-column>
                            }
                        </kendo-grid-column-group>
                    } @else {
                        <kendo-grid-column
                            [field]="item.field"
                            [title]="item.title | translate"
                            [width]="item.width"
                            [filterable]="!!item.filterable"
                            [sortable]="!!item.sortable"
                            [filter]="item.type === 'number' ? 'numeric' : item.type === 'boolean' ? 'boolean' : 'text'"
                            [class]="{ left: item.left, centered: !item.left, 'sticky-left': item.stickyLeft !== undefined, 'sticky-edge': item.stickyEdge }"
                            [headerClass]="{ 'sticky-left': item.stickyLeft !== undefined, 'sticky-edge': item.stickyEdge }"
                            [filterClass]="{ 'sticky-left': item.stickyLeft !== undefined, 'sticky-edge': item.stickyEdge }"
                            [style]="item.stickyLeft === undefined ? null : { left: item.stickyLeft + 'px' }"
                            [headerStyle]="item.stickyLeft === undefined ? null : { left: item.stickyLeft + 'px' }"
                            [filterStyle]="item.stickyLeft === undefined ? null : { left: item.stickyLeft + 'px' }"
                            [resizable]="item.stickyLeft === undefined"
                        >
                            <ng-template kendoGridCellTemplate let-row>
                                <ng-container *ngTemplateOutlet="cell; context: { $implicit: row, column: item }"></ng-container>
                            </ng-template>
                        </kendo-grid-column>
                    }
                }
            </kendo-grid-column-group>
        }

        <!-- Held at the right edge by its classes: Kendo's locked and sticky columns stay on the left and drop the detail rows. -->
        <kendo-grid-command-column [title]="'FLIGHT_LIST.actions' | translate" [width]="96" [class]="'actions-cell'" [headerClass]="'actions-cell'" [filterClass]="'actions-cell'">
            <ng-template kendoGridCellTemplate let-row>
                @let original = row | original;
                <!-- Only a flight with a sequence can be edited: -->
                <!-- @if (row.flightSequence?.trim()) { -->
                <button kendoButton class="edit-action" fillMode="solid" [svgIcon]="icons.edit" [title]="'FLIGHT_LIST.edit.action' | translate" (click)="editing.set(original)">
                    {{ "FLIGHT_LIST.edit.action" | translate }}
                </button>
                <!-- } -->
            </ng-template>
        </kendo-grid-command-column>

        <ng-template kendoGridDetailTemplate let-row>
            <app-flight-detail [flight]="row" [timeZone]="timeZone()"></app-flight-detail>
        </ng-template>

        <ng-template kendoGridNoRecordsTemplate>
            @if (failed()) {
                <span>{{ "FLIGHT_LIST.loadFailed" | translate }}</span>
                <button kendoButton fillMode="flat" themeColor="primary" (click)="load()">{{ "FLIGHT_LIST.retry" | translate }}</button>
            } @else if (searchError(); as error) {
                {{ error | translate }}
            } @else {
                {{ "FLIGHT_LIST.empty.flights" | translate }}
            }
        </ng-template>
    </kendo-grid>
</div>

@if (hint(); as hint) {
    <kendo-popup
        [anchor]="hint.anchor"
        [anchorAlign]="{ horizontal: 'center', vertical: 'top' }"
        [popupAlign]="{ horizontal: 'center', vertical: 'bottom' }"
        [margin]="{ horizontal: 0, vertical: 6 }"
        [animate]="false"
    >
        <div class="cell-hint">
            @if (hint.hook; as hook) {
                @if (hook.name) {
                    <strong>{{ hook.name }}</strong>
                }
                <span>{{ "FLIGHT_LIST.hook.serial" | translate }}: {{ hook.serial ?? "—" }}</span>
            } @else if (hint.hoist; as hoist) {
                @if (hoist.name) {
                    <strong>{{ hoist.name }}</strong>
                }
                <span>{{ "FLIGHT_LIST.hoist.serial" | translate }}: {{ hoist.serial ?? "—" }}</span>
            } @else if (hint.airport; as airport) {
                <strong>{{ airport.name ?? airport.code }}</strong>
                @if (airport.code) {
                    <span>{{ "FLIGHT_LIST.airport.code" | translate }}: {{ airport.code }}</span>
                }
                @if (airport.city || airport.countryCode) {
                    <span class="country">
                        @if (airport.country; as country) {
                            <img [src]="country.flag" alt="" width="20" height="15" />
                        }
                        {{ airport.city }}{{ airport.city && airport.countryCode ? ", " : "" }}{{ airport.country?.name ?? airport.countryCode }}
                    </span>
                }
                @if (airport.timezone) {
                    <span>
                        {{ airport.timezone }}
                        @if (airport.offset === 0) {
                            · {{ "FLIGHT_LIST.airport.sameTime" | translate }}
                        } @else if (airport.offset !== null && airport.offset > 0) {
                            · {{ "FLIGHT_LIST.airport.ahead" | translate: { time: airport.offsetText } }}
                        } @else if (airport.offset !== null) {
                            · {{ "FLIGHT_LIST.airport.behind" | translate: { time: airport.offsetText } }}
                        }
                    </span>
                }
            } @else {
                <span>{{ hint.roleName }}</span>
            }
        </div>
    </kendo-popup>
}

@if (editing(); as flight) {
    <app-flight-edit-dialog [flight]="flight" (saved)="onEdited()" (closed)="editing.set(null)"></app-flight-edit-dialog>
}
